import Box from "@mui/material/Box";
import ButtonGroup from "@mui/material/ButtonGroup";
import {styled} from "@mui/material/styles";
import {FC, PropsWithChildren, ReactElement, useCallback, useEffect, useState} from "react";
import IconButton from "@mui/material/IconButton";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import LockOutlined from "@mui/icons-material/LockOutlined";
import LockOpenOutlined from "@mui/icons-material/LockOpenOutlined";
import DeleteButton from "./Section/DeleteButton.tsx";
import HelpButton from "./HelpButton.tsx";
import {doNotForwardProps} from "../lib/styled.ts";

const OuterContainer = styled(Box)({
    overflow: 'hidden',
    padding: 0,
    margin: 0,
})

type ContainerProps = {
    collapse: boolean
}

const Container = styled(Box, doNotForwardProps('collapse'))<ContainerProps>(({theme, collapse}) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    transition: 'margin-top 0.5s ease-in-out',
    marginTop: collapse ? '-80%' : 0,

    '&.editable:hover': {
        '.inner-container': {
            border: `2px solid ${theme.palette.primary.light}`,
        },
        '.button-group': {
            border: `2px solid ${theme.palette.primary.light}`,
            opacity: 1
        }
    },
    '&.editable:has(:focus), &.editable:has(.Mui-focused)': {
        '.inner-container': {
            border: `2px solid ${theme.palette.primary.main}`,
        },
        '.button-group': {
            border: `2px solid ${theme.palette.primary.main}`,
            opacity: 1
        }
    }
}))

type InnerContainerProps = {
    padding?: number | string;
    hasToolbar?: boolean;
}

const InnerContainer = styled(Box, doNotForwardProps('padding', 'hasToolbar'))<InnerContainerProps>(({padding, hasToolbar}) => ({
    marginTop: -2,
    borderRadius: `${hasToolbar ? '0' : '10px'} 0 10px 10px`,
    padding: padding == undefined ? '3px 15px 0 15px' : padding,
    border: '2px solid transparent',
    transition: 'border 0.3s',
    overflow: 'hidden',
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 30,
}))

const ButtonGroupStyled = styled(ButtonGroup)(({theme}) => ({
    margin: "0",
    boxShadow: 'none',
    borderRadius: '10px 10px 0 0',
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.secondary.main,
    opacity: 0,
    transition: 'opacity 0.3s',
}))

export type SectionProps = PropsWithChildren<{
    isFirst: boolean
    isLast: boolean
    collapse: boolean
    uncollapse: boolean
    sectionID: string
    isPublic: boolean
    editMode: boolean
    padding?: number | string;
    toolbar?: ReactElement;
    onMoveUp: (sectionID: string) => void
    onMoveDown: (sectionID: string) => void
    onDelete: (sectionID: string) => void
    onCollapsed: (sectionID: string) => void
    onChangeIsPublic: (sectionID: string, isPublic: boolean) => void
}>

const Section: FC<SectionProps> = ({
    isFirst,
    isLast,
    uncollapse,
    collapse,
    sectionID,
    isPublic,
    editMode,
    padding,
    toolbar,
    onMoveUp,
    onMoveDown,
    onDelete,
    onCollapsed,
    onChangeIsPublic,
    children
}) => {
    const [rendered, setRendered] = useState<boolean>(false)

    useEffect(() => {
        const id = setTimeout(() => setRendered(true), 10)
        return () => clearTimeout(id)
    }, [])

    useEffect(() => {
        if (collapse) {
            const id = setTimeout(() => onCollapsed(sectionID), 500)
            return () => clearTimeout(id)
        }
    }, [collapse, onCollapsed, sectionID])

    const handleMoveUp = useCallback(() => onMoveUp(sectionID), [onMoveUp, sectionID])
    const handleMoveDown = useCallback(() => onMoveDown(sectionID), [onMoveDown, sectionID])
    const handleDelete = useCallback(() => onDelete(sectionID), [onDelete, sectionID])
    const handleTogglePublicFlag = useCallback(() => onChangeIsPublic(sectionID, !isPublic), [onChangeIsPublic, sectionID, isPublic])

    return (
        <OuterContainer>
            <Container collapse={collapse || uncollapse && !rendered} className={editMode ? "editable" : undefined}>
                <Box display="flex" flexDirection="row" width="100%">
                    {toolbar ? (
                        <Box flexGrow={1} display="flex" flexDirection="row">
                            <ButtonGroupStyled className="button-group" size="small" variant="contained">
                                {toolbar}
                            </ButtonGroupStyled>
                        </Box>
                    ) : null}
                    <Box flexGrow={1} display="flex" flexDirection="row-reverse">
                        <ButtonGroupStyled className="button-group" size="small" variant="contained">
                            <HelpButton help="TODO" variant="button" />
                            <IconButton onClick={handleTogglePublicFlag} size="small" color={isPublic ? "warning" : "success"}>
                                {isPublic ? <LockOpenOutlined /> : <LockOutlined />}
                            </IconButton>
                            <IconButton onClick={handleMoveUp} size="small" disabled={isFirst}><ArrowUpward/></IconButton>
                            <IconButton onClick={handleMoveDown} size="small" disabled={isLast}><ArrowDownward/></IconButton>
                            <DeleteButton onClick={handleDelete}/>
                        </ButtonGroupStyled>
                    </Box>
                </Box>
                <InnerContainer className="inner-container" padding={padding} hasToolbar={Boolean(toolbar)}>
                    {children}
                </InnerContainer>
            </Container>
        </OuterContainer>
    )
}


export default Section
